import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable, BehaviorSubject, defer, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { switchMap, catchError, finalize, map } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import {
  WhatsAppStatusResponse,
  WhatsAppConnectResponse,
  WhatsAppDisconnectResponse,
  RateLimitStatusResponse,
  OTPStatsResponse,
  CleanupCacheResponse,
  OTPListResponse,
  OTPListQuery,
  SendTestOTPResponse,
  WhatsAppNumber,
  WhatsAppNumberStatus,
  AddWhatsAppNumberRequest,
} from '../interfaces/whatsapp-admin.interface';

// Interfaz para la respuesta envuelta del backend
interface ApiResponse<T> {
  success: boolean;
  data: T;
  metadata?: any;
}

type HttpMethod = 'GET' | 'POST' | 'DELETE';

interface RequestOptions {
  body?: unknown;
  params?: HttpParams;
  /**
   * true = acción del operador (conectar, quitar, limpiar...): prende el
   * loading$ global, que muestra el spinner y deshabilita botones.
   * Las lecturas (y en especial el polling cada 3s) NO lo prenden: si lo
   * hicieran, la pantalla parpadearía y los botones quedarían deshabilitados
   * a intervalos mientras se espera un QR.
   */
  action?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WhatsAppAdminService {
  private apiUrl = `${environment.apiKyc}otp/whatsapp`;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  /** Hay alguna acción del operador en curso (no incluye lecturas de fondo). */
  public loading$ = this.loadingSubject.asObservable();
  // Contador en vez de booleano: con dos acciones en paralelo, la primera en
  // terminar no debe apagar el loading mientras la otra sigue en vuelo.
  private pendingActions = 0;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token}`);
  }

  /**
   * Bloque común de todas las llamadas: headers con el token, desenvolver
   * `{success, data}` y normalizar el error. Va dentro de `defer` para que el
   * loading se prenda al suscribirse (no al llamar al método) y siempre se
   * apague en el finalize.
   */
  private request<T>(
    method: HttpMethod,
    path: string,
    options: RequestOptions = {},
  ): Observable<T> {
    const url = `${this.apiUrl}/${path}`;
    return defer(() => {
      if (options.action) this.setActionLoading(+1);
      return from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.request<ApiResponse<T>>(method, url, {
            headers,
            body: options.body,
            params: options.params,
          }),
        ),
        map((response) => response.data),
        catchError((error) => this.handleError(error)),
        finalize(() => {
          if (options.action) this.setActionLoading(-1);
        }),
      );
    });
  }

  private setActionLoading(delta: number): void {
    this.pendingActions = Math.max(0, this.pendingActions + delta);
    this.loadingSubject.next(this.pendingActions > 0);
  }

  /** El id va en la URL: se escapa por si algún día deja de ser un uuid. */
  private numberPath(id: string, suffix = ''): string {
    return `numbers/${encodeURIComponent(id)}${suffix}`;
  }

  /**
   * Mensaje que manda el backend, si lo hay. El filtro global responde
   * `{success:false, error:{message}}`; algunos errores de Nest vienen como
   * `{message}` (a veces un array, en las validaciones del DTO).
   */
  private extractBackendMessage(body: any): string | null {
    const raw = body?.error?.message ?? body?.message;
    if (Array.isArray(raw)) return raw.filter(Boolean).join('. ') || null;
    if (typeof raw === 'string' && raw.trim()) return raw;
    if (typeof body === 'string' && body.trim()) return body;
    return null;
  }

  private handleError(error: HttpErrorResponse | Error) {
    // Falla antes de salir la request (p.ej. no hay sesión de Cognito)
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => ({
        statusCode: 0,
        message: error?.message || 'Ha ocurrido un error en el servicio',
      }));
    }

    let customError: any = {
      statusCode: error.status,
      message: 'Ha ocurrido un error en el servicio',
      ...(typeof error.error === 'object' ? error.error : {}),
    };

    if (error.error instanceof ErrorEvent) {
      customError.message = `Error: ${error.error.message}`;
      return throwError(() => customError);
    }

    // Primero el mensaje real del backend ("El número base no se puede
    // quitar", "Máximo 60 caracteres"...): los textos fijos por status de
    // abajo son solo el respaldo cuando no vino ninguno.
    const backendMessage =
      error.status !== 0 ? this.extractBackendMessage(error.error) : null;
    if (backendMessage) {
      customError.message = backendMessage;
      return throwError(() => customError);
    }

    switch (error.status) {
      case 0:
        customError.message =
          'No se pudo conectar al servidor. Verifique la URL o CORS.';
        break;
      case 401:
        customError.message =
          'Error de autenticación. Por favor, inicie sesión nuevamente.';
        break;
      case 403:
        customError.message = 'No tiene permisos para realizar esta acción.';
        break;
      case 400:
        customError.message = 'Solicitud inválida.';
        break;
      case 404:
        customError.message = 'El servicio no está disponible en este momento.';
        break;
      case 500:
        customError.message =
          'Error interno del servidor. Por favor, intente más tarde.';
        break;
      default:
        customError.message = `Error del servidor: ${error.status}, ${error.message}`;
    }
    return throwError(() => customError);
  }

  // --- Endpoints del número base (compatibilidad) ---

  getStatus(): Observable<WhatsAppStatusResponse> {
    return this.request<WhatsAppStatusResponse>('GET', 'status');
  }

  connect(): Observable<WhatsAppConnectResponse> {
    return this.request<WhatsAppConnectResponse>('POST', 'connect', {
      body: {},
      action: true,
    });
  }

  disconnect(): Observable<WhatsAppDisconnectResponse> {
    return this.request<WhatsAppDisconnectResponse>('POST', 'disconnect', {
      body: {},
      action: true,
    });
  }

  // --- Globales ---

  getRateLimitStatus(): Observable<RateLimitStatusResponse> {
    return this.request<RateLimitStatusResponse>('GET', 'rate-limit');
  }

  getOTPStats(): Observable<OTPStatsResponse> {
    return this.request<OTPStatsResponse>('GET', 'stats');
  }

  cleanupCache(): Observable<CleanupCacheResponse> {
    return this.request<CleanupCacheResponse>('POST', 'cleanup', {
      body: {},
      action: true,
    });
  }

  getOTPMessages(query: OTPListQuery): Observable<OTPListResponse> {
    let params = new HttpParams();
    if (query.page) params = params.set('page', query.page.toString());
    if (query.limit) params = params.set('limit', query.limit.toString());
    if (query.status) params = params.set('status', query.status);
    if (query.phoneNumber) params = params.set('phoneNumber', query.phoneNumber);

    // El historial tiene su propio spinner (loadingMessages en el componente)
    return this.request<OTPListResponse>('GET', 'messages', { params });
  }

  /**
   * `numberId` elige por qué número sale: 'base' (registro) o un extra, que
   * manda el mensaje marca blanca del pagador con `senderName` como ejemplo.
   */
  sendTestOTP(
    phoneNumber: string,
    numberId?: string,
    senderName?: string,
  ): Observable<SendTestOTPResponse> {
    return this.request<SendTestOTPResponse>('POST', 'send-test', {
      body: { phoneNumber, numberId, senderName: senderName || undefined },
      action: true,
    });
  }

  // --- Pool de números (base + extras para pagadores) ---

  /** Lista de números; el base viene primero. Lectura: no prende loading$. */
  getNumbers(): Observable<WhatsAppNumber[]> {
    return this.request<WhatsAppNumber[]>('GET', 'numbers');
  }

  /** Estado de un número con su QR (solo en QR_READY). Lectura de fondo. */
  getNumber(id: string): Observable<WhatsAppNumberStatus> {
    return this.request<WhatsAppNumberStatus>('GET', this.numberPath(id));
  }

  /** Crea un número extra (rol pagador) y arranca su conexión. */
  addNumber(label: string): Observable<WhatsAppNumber> {
    const body: AddWhatsAppNumberRequest = { label };
    return this.request<WhatsAppNumber>('POST', 'numbers', {
      body,
      action: true,
    });
  }

  /** Conecta o pide un QR nuevo. */
  connectNumber(id: string): Observable<WhatsAppNumberStatus> {
    return this.request<WhatsAppNumberStatus>(
      'POST',
      this.numberPath(id, '/connect'),
      { body: {}, action: true },
    );
  }

  /** Detiene la conexión o la espera de QR SIN borrar la sesión ("cancelar"). */
  stopNumber(id: string): Observable<WhatsAppNumberStatus> {
    return this.request<WhatsAppNumberStatus>(
      'POST',
      this.numberPath(id, '/stop'),
      { body: {}, action: true },
    );
  }

  /** Cierra la sesión y BORRA credenciales: obliga a re-escanear el QR. */
  disconnectNumber(id: string): Observable<WhatsAppDisconnectResponse> {
    return this.request<WhatsAppDisconnectResponse>(
      'POST',
      this.numberPath(id, '/disconnect'),
      { body: {}, action: true },
    );
  }

  /** Quita un número extra del pool (el base no se puede quitar: 404). */
  removeNumber(id: string): Observable<WhatsAppDisconnectResponse> {
    return this.request<WhatsAppDisconnectResponse>(
      'DELETE',
      this.numberPath(id),
      { action: true },
    );
  }
}
